import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {Shipment} from '../../../domain/shipment';
import {Carrier} from '../../../domain/carrier';
import {filter} from 'rxjs/operators';
import {EventListEntry} from './event-list-entry';
import {EventNamePipe} from '../../../component/event-name-pipe';
import {PopupService} from '../../../component/popup.service';
import {ConfirmationDialogComponent} from '../../../component/confirmation-dialog/confirmation-dialog.component';
import {Session} from '../../../domain/session';
import {DomSanitizer} from '@angular/platform-browser';
import {MatDialog} from '@angular/material/dialog';
import {AttachmentListComponent} from '../../../component/attachment-list/attachment-list.component';
import {AssignOrderCarrierComponent} from '../assign-carrier-dialog/assign-order-carrier.component';
import {MessageService} from '../../../component/message.service';
import {RequestExecutor} from '../../../util/request-executor';

@Component({
  selector: 'app-admin-shipment-details',
  templateUrl: './shipment-details.component.html',
  styleUrls: ['./shipment-details.component.css'],
  standalone: false
})
export class ShipmentDetailsComponent implements OnInit {
  shipment: Shipment;
  carriers: Carrier[];
  events: EventListEntry[];

  carrierName: string = null;

  progress = false;
  message = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private request: RequestExecutor,
    private eventNamePipe: EventNamePipe,
    private popupService: PopupService,
    private messageService: MessageService,
    private dialog: MatDialog,
    public session: Session,
    public domSanitizer: DomSanitizer
  ) {
    this.carriers = session.getCarriers();
  }

  ngOnInit() {
    this.initValue();

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.initValue();
      });
  }

  private initValue() {
    this.shipment = this.route.snapshot.data['shipment'];

    this.carrierName = this.getCarrierName(this.shipment.CarrierId);

    this.events = this.getEvents();
  }

  getCarrierName(carrierId: string) {
    if (this.carriers !== undefined) {
      const carrier = this.carriers.find(r => r.Id === carrierId);

      return carrier === undefined ? null : carrier.Name;
    }
    return null;
  }

  getEvents(): EventListEntry[] {
    let result: EventListEntry[] = [];

    this.shipment.Parcels.forEach(p => {
      let parcelDescriptions = this.parseParcelDescription(p.Description);

      p.Events.forEach(e => {
        result.push({
          createdAt: e.CreatedAt,
          attachmentIds: [],
          name: this.eventNamePipe.transform(e.Type),
          parcelDescription: parcelDescriptions[0],
          description: null,
          number: 'Kolli ' + p.Number,
          signature: e.Signature,
          username: e.UserDisplayName
        });
      });

      p.Deviations.forEach(d => {
        result.push({
          createdAt: d.CreatedAt,
          attachmentIds: d.AttachmentIds,
          name: d.DefinitionName.NO ?? 'Ukjent avvik',
          parcelDescription: parcelDescriptions[0],
          description: d.Comment,
          number: 'Kolli ' + p.Number,
          signature: null,
          username: d.UserDisplayName
        });
      });
    });

    this.shipment.Deviations.forEach(d => {
      result.push({
        createdAt: d.CreatedAt,
        attachmentIds: d.AttachmentIds,
        name: d.DefinitionName.NO ?? 'Ukjent avvik',
        parcelDescription: null,
        description: d.Comment,
        number: 'Sending ' + this.shipment.Number,
        signature: null,
        username: d.UserDisplayName
      });
    });

    result = result.sort((a, b) => {
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });

    return result;
  }

  showAttachments(attachmentIds: string[]) {
    this.popupService.openFromComponent(AttachmentListComponent, {attachmentIds: attachmentIds});
  }

  queryUnassign() {
    let dialogRef = this.dialog.open(ConfirmationDialogComponent);

    dialogRef.componentInstance.title = 'Bekreft endring';
    dialogRef.componentInstance.description = 'Vil du fjerne tildeling av sendingen?';

    dialogRef.afterClosed()
      .subscribe(value => {
        if (value) {
          this.unassign();
        }
      });
  }

  unassign() {
    this.progress = true;

    this.request.post('/order/unassign/batch', {OrderIds: [this.shipment.OrderId]})
      .then(() => {
        this.messageService.showBottomSuccessSnackBar('Tildeling ble fjernet for sendingen');

        this.router.navigate(['admin', 'shipment', this.shipment.Number]);
      })
      .catch(error => {
        if (error.status == 401) {
          this.router.navigate(['login']);
        } else {
          this.messageService.showBottomErrorSnackBar('Kunne ikke fjerne tildeling av sendingen');
        }
      })
      .finally(() => this.progress = false);
  }

  assign() {
    this.popupService.openFromComponent(AssignOrderCarrierComponent, {orderIds: [this.shipment.OrderId]})
      .subscribe(() => {
        this.router.navigate(['admin', 'shipments', 'assign']);
      });
  }

  queryDelete() {
    let dialogRef = this.dialog.open(ConfirmationDialogComponent);

    dialogRef.componentInstance.title = 'Bekreft sletting';
    dialogRef.componentInstance.description = 'Vil du slette sendingen?';

    dialogRef.afterClosed()
      .subscribe(value => {
        if (value) {
          this.delete();
        }
      });
  }

  delete() {
    this.progress = true;

    this.request.delete('/shipment', {id: this.shipment.Id})
      .then(() => {
        this.messageService.showBottomSuccessSnackBar('Sending ' + this.shipment.Number + ' ble slettet');

        this.router.navigate(['/admin/shipments']);
      })
      .catch(error => {
        if (error.status == 401) {
          this.router.navigate(['login']);
        } else {
          this.messageService.showBottomErrorSnackBar('Kunne ikke slette sending');
        }
      })
      .finally(() => this.progress = false);
  }

  private parseParcelDescription(description: string): string[] {
    try {
      if (description.startsWith('[')) {
        return JSON.parse(description);
      }
      return [description];
    } catch {
      return [description];
    }
  }

  printLabel() {
    this.progress = true;

    this.request.post('/print/shipmentlabels', null, {shipmentNumber: this.shipment.Number})
      .then(() => {
        this.messageService.showBottomSuccessSnackBar('Etiketter ble skrevet ut');
      })
      .catch(error => {
        if (error.status == 401) {
          this.router.navigate(['login']);
        } else {
          this.messageService.showBottomErrorSnackBar('Kunne ikke skrive ut etiketter');
        }
      })
      .finally(() => this.progress = false);
  }
}
